/**
 * Kassen und Terminals (Spec 2.3, 10.1, 10.2).
 *
 * Terminals werden getauscht, Kassen kommen und gehen. Beides muss ohne
 * Deployment gehen. Historische Zuordnungen bleiben trotzdem korrekt, weil
 * jede Rohzeile ihre Kasse beim Import festhält und die Gültigkeitsdaten
 * zum Betriebstag aufgelöst werden, nie zum heutigen Datum.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PoolDto, type WorkstationDto } from "../../lib/api";
import {
  Empty,
  ErrorBox,
  Info,
  Section,
  Spinner,
  StatusChip,
} from "../../components/ui";

interface Draft {
  name: string;
  posTerminalId: string;
  poolId: string;
  excludedFromPool: boolean;
  manualOnly: boolean;
  validFrom: string;
  validTo: string;
}

const EMPTY: Draft = {
  name: "",
  posTerminalId: "",
  poolId: "",
  excludedFromPool: false,
  manualOnly: false,
  validFrom: "",
  validTo: "",
};

export function Workstations() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const queryClient = useQueryClient();

  const workstations = useQuery({
    queryKey: ["workstations"],
    queryFn: () => api.get<{ workstations: WorkstationDto[] }>("/workstations"),
  });
  const pools = useQuery({
    queryKey: ["pools"],
    queryFn: () => api.get<{ pools: PoolDto[] }>("/pools"),
  });

  const toPayload = (d: Draft) => ({
    name: d.name.trim(),
    posTerminalId: d.posTerminalId.trim() || null,
    poolId: d.poolId || null,
    excludedFromPool: d.excludedFromPool,
    manualOnly: d.manualOnly,
    validFrom: d.validFrom || null,
    validTo: d.validTo || null,
  });

  const create = useMutation({
    mutationFn: () => api.post("/workstations", toPayload(draft)),
    onSuccess: async () => {
      setAdding(false);
      setDraft(EMPTY);
      await queryClient.invalidateQueries();
    },
  });

  const update = useMutation({
    mutationFn: (id: string) => api.patch(`/workstations/${id}`, toPayload(draft)),
    onSuccess: async () => {
      setEditing(null);
      setDraft(EMPTY);
      await queryClient.invalidateQueries();
    },
  });

  const toggleActive = useMutation({
    mutationFn: (w: WorkstationDto) => api.patch(`/workstations/${w.id}`, { active: !w.active }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const startEdit = (w: WorkstationDto) => {
    setEditing(w.id);
    setAdding(false);
    setDraft({
      name: w.name,
      posTerminalId: w.posTerminalId ?? "",
      poolId: w.poolId ?? "",
      excludedFromPool: w.excludedFromPool,
      manualOnly: w.manualOnly ?? false,
      validFrom: w.validFrom ?? "",
      validTo: w.validTo ?? "",
    });
  };

  if (workstations.isLoading) return <Spinner />;

  const rows = workstations.data?.workstations ?? [];
  const poolList = pools.data?.pools ?? [];

  return (
    <Section
      label="Verkaufsstellen"
      title="Kassen und Terminals"
      actions={
        <button
          className="btn-primary"
          onClick={() => {
            setAdding((v) => !v);
            setEditing(null);
            setDraft(EMPTY);
          }}
        >
          {adding ? "Abbrechen" : "Kasse anlegen"}
        </button>
      }
    >
      {adding ? (
        <Form
          draft={draft}
          setDraft={setDraft}
          pools={poolList}
          error={create.error}
          pending={create.isPending}
          submitLabel="Anlegen"
          onSubmit={() => create.mutate()}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      {rows.length === 0 ? (
        <Empty>Noch keine Kassen angelegt.</Empty>
      ) : (
        <div className="card overflow-hidden">
          <table className="tbl">
            <thead>
              <tr>
                <th>Kasse</th>
                <th className="w-36">
                  Terminal-ID
                  <Info text="Die ID des Karten-Terminals aus dem VR-Export. Wird ein Terminal getauscht, hier die neue ID eintragen; bereits importierte Zeilen behalten ihre Kasse." />
                </th>
                <th className="w-48">Pool</th>
                <th className="w-44">
                  Gültigkeit
                  <Info text="Optional. Begrenzt, ab wann und bis wann die Kasse verwendet wird. Die Berechnung löst das immer zum Betriebstag auf, nie zum heutigen Datum." />
                </th>
                <th className="w-32">Status</th>
                <th className="w-56"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) =>
                editing === w.id ? (
                  <tr key={w.id}>
                    <td colSpan={6} className="bg-surface p-0">
                      <div className="p-4">
                        <Form
                          draft={draft}
                          setDraft={setDraft}
                          pools={poolList}
                          error={update.error}
                          pending={update.isPending}
                          submitLabel="Speichern"
                          onSubmit={() => update.mutate(w.id)}
                          onCancel={() => setEditing(null)}
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={w.id} className={w.active ? "" : "opacity-50"}>
                    <td>{w.name}</td>
                    <td className="font-mono text-xs text-muted">{w.posTerminalId ?? "–"}</td>
                    <td className="text-muted">
                      {w.excludedFromPool ? "–" : (w.poolCode ?? "kein Pool")}
                    </td>
                    <td className="text-xs text-muted">
                      {w.validFrom || w.validTo
                        ? `${w.validFrom ?? "immer"} bis ${w.validTo ?? "offen"}`
                        : "unbegrenzt"}
                    </td>
                    <td>
                      {w.manualOnly ? (
                        <span
                          className="chip-warn"
                          title="Steht in keinem Export. Zahlen werden am Betriebstag von Hand erfasst."
                        >
                          nur von Hand
                        </span>
                      ) : w.excludedFromPool ? (
                        <span
                          className="chip-muted"
                          title="Wird importiert und angezeigt, fließt aber nie in einen Trinkgeldtopf. Gilt für die Abendkasse."
                        >
                          ohne Topf
                        </span>
                      ) : (
                        <StatusChip status={w.active ? "ACTIVE" : "INACTIVE"} />
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <button className="btn-ghost" onClick={() => startEdit(w)}>
                        Bearbeiten
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => toggleActive.mutate(w)}
                        title={
                          w.active
                            ? "Deaktivieren. Historische Daten bleiben erhalten."
                            : "Wieder aktivieren."
                        }
                      >
                        {w.active ? "Deaktivieren" : "Aktivieren"}
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-muted">
        Kassen werden nie gelöscht, nur deaktiviert. Beim Import wird die Kasse über den
        Namen aus den ecos-Abschlüssen und die Terminal-ID aus dem VR-Export gefunden. Taucht
        eine unbekannte ID auf, meldet der Import das und die Kasse lässt sich hier anlegen.
      </p>
    </Section>
  );
}

function Form({
  draft,
  setDraft,
  pools,
  error,
  pending,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  pools: PoolDto[];
  error: unknown;
  pending: boolean;
  submitLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft({ ...draft, [key]: value });

  return (
    <form
      className="card mb-6 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block">
          <span className="label mb-1 block">
            Name
            <Info text="Muss exakt der Spalte „Mitarbeiter“ in den ecos-Kassenabschlüssen entsprechen, sonst wird der Abschluss nicht zugeordnet." />
          </span>
          <input
            className="field"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Nebentheke 5"
            required
          />
        </label>

        <label className="block">
          <span className="label mb-1 block">Terminal-ID</span>
          <input
            className="field font-mono"
            value={draft.posTerminalId}
            onChange={(e) => set("posTerminalId", e.target.value)}
            placeholder="64062598"
          />
        </label>

        <label className="block">
          <span className="label mb-1 block">Pool</span>
          <select
            className="field"
            value={draft.poolId}
            disabled={draft.excludedFromPool}
            onChange={(e) => set("poolId", e.target.value)}
          >
            <option value="">– kein Pool –</option>
            {pools
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>

        <label className="block">
          <span className="label mb-1 block">gültig ab</span>
          <input
            type="date"
            className="field"
            value={draft.validFrom}
            onChange={(e) => set("validFrom", e.target.value)}
          />
        </label>

        <label className="block">
          <span className="label mb-1 block">gültig bis</span>
          <input
            type="date"
            className="field"
            value={draft.validTo}
            onChange={(e) => set("validTo", e.target.value)}
          />
        </label>

        <label className="flex items-end gap-2 pb-2">
          <input
            type="checkbox"
            checked={draft.excludedFromPool}
            onChange={(e) => set("excludedFromPool", e.target.checked)}
          />
          <span className="text-sm">
            Kein Trinkgeldtopf
            <Info text="Für Kassen wie die Abendkasse: Umsätze werden importiert und angezeigt, fließen aber nie in einen Topf." />
          </span>
        </label>

        <label className="flex items-end gap-2 pb-2">
          <input
            type="checkbox"
            checked={draft.manualOnly}
            onChange={(e) => set("manualOnly", e.target.checked)}
          />
          <span className="text-sm">
            Nur von Hand
            <Info text="Für Geräte, die in keinem der drei Exporte stehen, etwa die Wintergarten-Terminals einer anderen Firma. Sie werden nie automatisch zugeordnet; ihre Zahlen trägt man am Betriebstag ein, wenn sie gelaufen sind." />
          </span>
        </label>
      </div>

      {error ? (
        <div className="mt-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button type="submit" className="btn-primary" disabled={pending || !draft.name.trim()}>
          {pending ? "Speichert" : submitLabel}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
